'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'
import type { SampleProject } from '@ideight/api-client'
import { api } from '../../lib/api'
import { FloorplanThumb } from '../_components/FloorplanThumb'

/**
 * Public sample gallery — admin-published sample homes, browsable without
 * sign-in. Each card opens a read-only 3D view (`/samples/[id]`). Linked
 * from the landing as "Browse projects".
 */
export default function SamplesPage() {
  const [samples, setSamples] = useState<readonly SampleProject[] | null>(null)

  useEffect(() => {
    let cancelled = false
    void api
      .listSamples()
      .then((s) => {
        if (!cancelled) setSamples(s)
      })
      .catch(() => {
        if (!cancelled) setSamples([])
      })
    return () => {
      cancelled = true
    }
  }, [])

  return (
    <main style={pageStyle}>
      <header style={{ marginBottom: 28 }}>
        <Link href="/" style={{ color: '#999', fontSize: 13 }}>
          ← Tinkerspace
        </Link>
        <h1 style={{ margin: '12px 0 6px', fontSize: 30 }}>Sample homes</h1>
        <p style={{ color: '#9a948b', marginTop: 0 }}>
          Explore HDB BTO homes designed in Tinkerspace — walk through them in 3D, no sign-up
          needed.
        </p>
      </header>

      {samples === null ? (
        <p style={{ color: '#888' }}>Loading…</p>
      ) : samples.length === 0 ? (
        <p style={{ color: '#888' }}>No sample homes published yet — check back soon.</p>
      ) : (
        <div style={gridStyle}>
          {samples.map((s) => (
            <Link key={s.id} href={`/samples/${s.id}`} style={cardStyle}>
              <div style={cardThumbStyle}>
                <FloorplanThumb sampleId={s.id} />
              </div>
              <div style={cardBodyStyle}>
                <span style={{ color: '#ece9e3', fontWeight: 600 }}>{s.name}</span>
                {s.featured ? <span style={badgeStyle}>Featured</span> : null}
                <span style={{ color: '#9a948b', fontSize: 12 }}>Open in 3D →</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <p style={{ marginTop: 32 }}>
        <Link href="/sign-up" style={{ color: '#c9a36a' }}>
          Start your own design →
        </Link>
      </p>
    </main>
  )
}

const pageStyle: React.CSSProperties = {
  minHeight: '100vh',
  background: '#111110',
  color: '#ece9e3',
  fontFamily: '-apple-system, BlinkMacSystemFont, system-ui, sans-serif',
  padding: '48px 28px',
  maxWidth: 1000,
  margin: '0 auto',
}
const gridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 16,
}
const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  border: '1px solid rgba(236,233,227,0.12)',
  borderRadius: 12,
  overflow: 'hidden',
  background: '#161614',
  textDecoration: 'none',
}
const cardThumbStyle: React.CSSProperties = {
  height: 120,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'radial-gradient(120% 90% at 70% 0%, #211e18 0%, #131210 62%)',
  color: '#6f6a61',
}
const cardBodyStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  padding: '12px 14px',
}
const badgeStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: '#1a1408',
  background: '#c9a36a',
  borderRadius: 4,
  padding: '2px 6px',
}
